// Google 로그인(선택 연결): Google Identity Services 공식 버튼(renderButton, popup)으로 ID 토큰 한 장을 받는다.
// blade-surge src/social/google-signin.js를 옮겼다. One Tap(prompt)은 쓰지 않는다(쿨다운·로그아웃 상태에서 막힘).
// 스크립트는 버튼을 누를 때만 불러온다(첫 화면 자원·외부 요청 0). 토큰은 서버로 한 번 보내고 저장·기록하지 않는다.
// 취소·차단·시간 초과·스크립트 실패는 null(던지지 않음). 개발 전용: localhost에서 ?devsub=<id>면 GIS 대신 "dev:<id>" 테스트 신원.
import { DEV_TOKEN_PREFIX, GOOGLE_WEB_CLIENT_ID } from './protocol';
import { C, FONT } from '../ui/dom';
import { LEGAL_LINKS } from '../community/config';

const GSI_SRC = 'https://accounts.google.com/gsi/client';
const LOAD_TIMEOUT_MS = 12_000;
const MAX_WAIT_MS = 120_000;

interface Gsi {
  initialize(o: { client_id: string; ux_mode: 'popup'; auto_select: boolean; callback: (r: { credential?: string }) => void }): void;
  renderButton(parent: HTMLElement, o: Record<string, unknown>): void;
}
const gsi = (): Gsi | null => (globalThis as unknown as { google?: { accounts?: { id?: Gsi } } }).google?.accounts?.id ?? null;

let loading: Promise<void> | null = null;
function loadGsi(): Promise<void> {
  if (gsi()) return Promise.resolve();
  if (loading) return loading;
  loading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    let timer = 0;
    const fail = (why: string): void => {
      clearTimeout(timer); loading = null;
      try { script.remove(); } catch { /* 이미 없음 */ }
      reject(new Error(why));
    };
    script.src = GSI_SRC; script.async = true;
    script.onload = () => { clearTimeout(timer); if (gsi()) resolve(); else fail('gsi-missing'); };
    script.onerror = () => fail('gsi-load');
    timer = window.setTimeout(() => fail('gsi-timeout'), LOAD_TIMEOUT_MS);
    document.head.append(script);
  });
  return loading;
}

/** 개발 전용 테스트 신원(localhost + ?devsub=). 운영 호스트에서는 항상 null이고 서버도 받지 않는다 */
export function devSubject(): string | null {
  if (typeof location === 'undefined' || !/^(localhost|127\.0\.0\.1)$/.test(location.hostname)) return null;
  const v = new URLSearchParams(location.search).get('devsub');
  return v && /^[A-Za-z0-9_-]{1,64}$/.test(v) ? v : null;
}

let active: Promise<string | null> | null = null;

/** Google ID 토큰(또는 개발 테스트 토큰). 취소·실패는 null. 동시에 여러 번 불러도 대화상자 하나를 함께 기다린다 */
export function googleSignIn(): Promise<string | null> {
  const dev = devSubject();
  if (dev) return Promise.resolve(DEV_TOKEN_PREFIX + dev);
  if (active) return active;
  active = new Promise<string | null>((resolve) => {
    let done = false;
    const cleanups: (() => void)[] = [];
    const finish = (v: string | null): void => {
      if (done) return;
      done = true; active = null;
      while (cleanups.length) { try { cleanups.pop()!(); } catch { /* 정리 오류 무시 */ } }
      resolve(v);
    };
    try {
      // 게임 UI와 같은 금테 남색 대화상자(가운데, 설정 창 위). 바깥 누르기·취소·Esc = 취소
      const overlay = document.createElement('div');
      overlay.style.cssText = 'position:fixed;inset:0;z-index:50;display:flex;align-items:center;justify-content:center;background:rgba(6,10,22,.6);pointer-events:auto;';
      const box = document.createElement('div');
      box.setAttribute('role', 'dialog'); box.setAttribute('aria-modal', 'true'); box.setAttribute('aria-label', 'Google로 계정 연결');
      box.dataset.testid = 'google-dialog';
      box.style.cssText = 'min-width:280px;max-width:calc(100vw - 32px);padding:18px 22px;border:2px solid ' + C.gold + ';border-radius:14px;background:linear-gradient(#222d52,#141b34);display:flex;flex-direction:column;gap:12px;align-items:center;font:600 15px ' + FONT + ';color:#e8eefc;box-sizing:border-box;';
      const note = document.createElement('div'); note.textContent = 'Google 로그인 창을 준비하고 있어요…';
      const slot = document.createElement('div'); slot.style.minHeight = '44px';
      const cancel = document.createElement('button');
      cancel.textContent = '취소';
      cancel.style.cssText = 'pointer-events:auto;cursor:pointer;font:800 16px ' + FONT + ';padding:8px 28px;border-radius:10px;border:2px solid ' + C.gold + ';background:#1A2238;color:' + C.gold + ';';
      cancel.addEventListener('click', () => finish(null));
      // 처음 연결 화면의 안내: 개인정보처리방침·이용약관·계정 삭제 안내(새 탭)
      const legal = document.createElement('div');
      legal.dataset.testid = 'google-legal';
      legal.style.cssText = 'max-width:260px;text-align:center;font:500 12px ' + FONT + ';color:#9fb0d8;line-height:1.7;';
      legal.append(document.createTextNode('연결하면 아래 안내를 확인한 것으로 봐요. 이메일·이름은 저장하지 않아요.'), document.createElement('br'));
      LEGAL_LINKS.forEach((link, i) => {
        if (i) legal.append(document.createTextNode(' · '));
        const a = document.createElement('a');
        a.textContent = link.label; a.href = link.href; a.target = '_blank'; a.rel = 'noopener noreferrer';
        a.style.cssText = 'color:#9fb0d8;text-decoration:underline;white-space:nowrap;pointer-events:auto;';
        legal.append(a);
      });
      box.append(note, slot, legal, cancel); overlay.append(box);
      overlay.addEventListener('pointerdown', (e) => { if (e.target === overlay) finish(null); });
      document.body.append(overlay);
      cleanups.push(() => overlay.remove());
      const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); finish(null); } };
      document.addEventListener('keydown', onKey, true);
      cleanups.push(() => document.removeEventListener('keydown', onKey, true));
      const maxTimer = window.setTimeout(() => finish(null), MAX_WAIT_MS);
      cleanups.push(() => clearTimeout(maxTimer));
      loadGsi().then(() => {
        const id = gsi();
        if (done || !id) return;
        id.initialize({
          client_id: GOOGLE_WEB_CLIENT_ID, ux_mode: 'popup', auto_select: false,
          callback: (r) => finish(r && typeof r.credential === 'string' && r.credential ? r.credential : null),
        });
        note.textContent = '아래 버튼으로 Google 계정을 선택해 주세요.';
        id.renderButton(slot, { type: 'standard', theme: 'filled_blue', size: 'large', text: 'signin_with', shape: 'pill', logo_alignment: 'left', width: 240, locale: 'ko' });
      }).catch(() => { note.textContent = 'Google 로그인을 불러오지 못했어요. 잠시 뒤 다시 시도해 주세요.'; });
    } catch { finish(null); }
  });
  return active;
}
